/**
 * Opens the property's third-party tour scheduler from site CTAs.
 *
 * When a scheduler widget is available, the popup opens on the current page
 * and navigation is cancelled. Priority:
 * 1. Perq scheduler — when Perq tools are installed (integration pending)
 * 2. Elise AI self-schedule tour (`window.eliseAi.onOpenSST`)
 *
 * If neither is available, the click handler navigates to the fallback URL
 * (typically `/schedule-a-tour`).
 *
 * Editor preview note: PreviewFrame listens for internal-link clicks in the
 * *capture* phase and posts VIBESITE_NAVIGATE before React bubble handlers
 * run. Schedule-a-Tour anchors must therefore use `scheduleTourHref()` so the
 * preview does not switch pages behind the popup.
 */

export type EliseAiApi = {
  onOpenSST?: () => void;
  onOpenChat?: () => void;
  onOpenCallUsWindow?: () => void;
  onOpenEmailUsWindow?: () => void;
};

declare global {
  interface Window {
    eliseAi?: EliseAiApi;
  }
}

type ScheduleTourClickEvent = {
  preventDefault: () => void;
  stopPropagation?: () => void;
  currentTarget?: EventTarget | null;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  button?: number;
};

const ELISE_API_RETRY_MS = 2000;
export const SCHEDULE_TOUR_FALLBACK_PATH = '/schedule-a-tour';

/** True when the theme is running inside the editor preview iframe. */
function isPreviewIframe(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

/**
 * Href for Schedule-a-Tour anchors.
 *
 * Always `#` so:
 * 1. PreviewFrame's capture-phase navigator ignores the click (it only routes
 *    real paths) — otherwise the preview switches to Schedule a Tour behind
 *    the popup.
 * 2. SSR and client markup match (no window checks during render).
 *
 * The real fallback lives on `data-schedule-tour-href`; the click handler
 * opens the in-page widget when available, or navigates to that fallback.
 */
export function scheduleTourHref(_fallbackHref?: string | null): string {
  return '#';
}

/**
 * Future Perq tools integration.
 *
 * When a Perq block is installed, detect its global API here and open the
 * Perq scheduler instead of Elise. Keep this ahead of Elise so Perq wins
 * when both are present.
 */
function isPerqSchedulerAvailable(): boolean {
  // Placeholder until the Perq block lands and exposes a known open API.
  return false;
}

function tryOpenPerqScheduler(): boolean {
  if (!isPerqSchedulerAvailable()) return false;
  return false;
}

function getEliseOpenSst(): (() => void) | undefined {
  const openSst = window.eliseAi?.onOpenSST;
  return typeof openSst === 'function' ? openSst : undefined;
}

/** True when Elise chat is on the page (API ready or still booting). */
function isEliseSchedulerAvailable(): boolean {
  if (getEliseOpenSst()) return true;
  // Widget shell means Elise is installed; API may still be hydrating.
  return Boolean(document.querySelector('.meetelise-chat'));
}

/**
 * Opens Elise SST on this page. If the chat shell is present but onOpenSST
 * is not ready yet, retry briefly instead of navigating away.
 */
function tryOpenEliseScheduler(): boolean {
  const openNow = getEliseOpenSst();
  if (openNow) {
    openNow();
    return true;
  }

  if (!document.querySelector('.meetelise-chat')) return false;

  const startedAt = Date.now();
  const retry = () => {
    const openSst = getEliseOpenSst();
    if (openSst) {
      openSst();
      return;
    }
    if (Date.now() - startedAt < ELISE_API_RETRY_MS) {
      window.requestAnimationFrame(retry);
    }
  };
  window.requestAnimationFrame(retry);
  return true;
}

/** True when a third-party in-page scheduler can be opened. */
export function canOpenScheduleTour(): boolean {
  if (typeof window === 'undefined') return false;
  if (isPerqSchedulerAvailable()) return true;
  if (isEliseSchedulerAvailable()) return true;
  return false;
}

/** Returns true if a third-party scheduler popup was opened on this page. */
export function openScheduleTour(): boolean {
  if (typeof window === 'undefined') return false;
  if (tryOpenPerqScheduler()) return true;
  if (tryOpenEliseScheduler()) return true;
  return false;
}

/** Navigate to the Schedule-a-Tour fallback (preview-aware). */
function navigateScheduleTourFallback(href: string): void {
  const raw = href || SCHEDULE_TOUR_FALLBACK_PATH;
  if (typeof window === 'undefined') return;

  if (isPreviewIframe()) {
    const hashAt = raw.indexOf('#');
    const hash = hashAt === -1 ? '' : raw.slice(hashAt);
    const pathAndSearch = hashAt === -1 ? raw : raw.slice(0, hashAt);
    const searchAt = pathAndSearch.indexOf('?');
    const search = searchAt === -1 ? '' : pathAndSearch.slice(searchAt);
    const path = searchAt === -1 ? pathAndSearch : pathAndSearch.slice(0, searchAt);
    const page = path.replace(/^\/+|\/+$/g, '') || 'home';
    window.parent.postMessage(
      { type: 'VIBESITE_NAVIGATE', payload: { page, search, hash } },
      '*',
    );
    return;
  }

  window.location.assign(
    raw.startsWith('/') || /^https?:/i.test(raw) ? raw : `/${raw}`,
  );
}

function readFallbackHref(event: ScheduleTourClickEvent): string {
  const el = event.currentTarget;
  if (el instanceof HTMLElement) {
    const fromData = el.getAttribute('data-schedule-tour-href');
    if (fromData && fromData !== '#') return fromData;
    if (el instanceof HTMLAnchorElement) {
      const href = el.getAttribute('href');
      if (href && href !== '#') return href;
    }
  }
  return SCHEDULE_TOUR_FALLBACK_PATH;
}

/**
 * Click handler for Schedule-a-Tour anchors.
 *
 * Always cancels the default click so the page never navigates behind an
 * in-page popup. Opens the widget when available; otherwise routes to the
 * fallback URL (`data-schedule-tour-href` or `/schedule-a-tour`).
 */
export function handleScheduleTourClick(
  event: ScheduleTourClickEvent,
): boolean {
  // Modified clicks: if we have a real fallback URL, open it in a new tab
  // (href may be `#` inside the preview iframe).
  if (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (event.button !== undefined && event.button !== 0)
  ) {
    if (canOpenScheduleTour()) {
      event.preventDefault();
      openScheduleTour();
      return true;
    }
    const fallback = readFallbackHref(event);
    if (fallback && fallback !== '#') {
      event.preventDefault();
      window.open(fallback, '_blank', 'noopener,noreferrer');
      return true;
    }
    return false;
  }

  event.preventDefault();
  event.stopPropagation?.();

  if (openScheduleTour()) return true;

  navigateScheduleTourFallback(readFallbackHref(event));
  return false;
}
